import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/**
 * Deliberately not react-router-dom - the task brief's dependency list for apps/checkout is
 * react/react-dom/@stripe/*; this app's routing needs are 4 simple paths, so a dependency-free
 * History API router keeps the bundle smaller and the dep list exactly what was asked for.
 */
export type Route =
  | { name: 'events' }
  | { name: 'event'; eventId: string }
  // `accessToken` (from createCheckoutSession's response) is REQUIRED to GET this session back as
  // a guest buyer - apps/api's GET /v1/checkout-sessions/:id rejects a non-secret-key caller with
  // no accessToken. Real bug found live: EventPage.tsx used to navigate here without it, and the
  // very next page always 400'd - no guest buyer had ever been able to reach the payment form.
  | { name: 'checkout'; checkoutSessionId: string; accessToken?: string }
  // `accessToken` here is the SAME guest-buyer mechanism as the `checkout` route above (from
  // `createCheckoutSession`'s response, forwarded by CheckoutPage.tsx once payment succeeds) -
  // added for the receipt-download feature (task #4). Previously this route carried no token at
  // all, so a guest buyer landing on their confirmation page had no way to authenticate a receipt
  // request; see this session's build report for a bigger, pre-existing bug this exposed in
  // packages/sdk (getOrder/getTicket never send accessToken to the backend at all) and in the
  // backend's own per-ticket access-token design (no endpoint ever issues one to the browser).
  | { name: 'order'; orderId: string; accessToken?: string }
  | { name: 'not-found' };

function parsePath(pathname: string, search: string): Route {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return { name: 'events' };
  if (segments[0] === 'events' && segments[1]) return { name: 'event', eventId: segments[1] };
  if (segments[0] === 'checkout' && segments[1]) {
    const accessToken = new URLSearchParams(search).get('accessToken') ?? undefined;
    return { name: 'checkout', checkoutSessionId: segments[1], accessToken };
  }
  if (segments[0] === 'orders' && segments[1]) {
    const accessToken = new URLSearchParams(search).get('accessToken') ?? undefined;
    return { name: 'order', orderId: segments[1], accessToken };
  }
  return { name: 'not-found' };
}

function routeToPath(route: Route): string {
  switch (route.name) {
    case 'events':
      return '/';
    case 'event':
      return `/events/${route.eventId}`;
    case 'checkout':
      return `/checkout/${route.checkoutSessionId}`;
    case 'order':
      return `/orders/${route.orderId}`;
    default:
      return '/';
  }
}

interface RouterContextValue {
  route: Route;
  navigate: (route: Route) => void;
}

const RouterContext = createContext<RouterContextValue | null>(null);

export function RouterProvider({ children }: { children: ReactNode }) {
  const [route, setRoute] = useState<Route>(() =>
    parsePath(window.location.pathname, window.location.search),
  );

  useEffect(() => {
    const onPopState = () =>
      setRoute(parsePath(window.location.pathname, window.location.search));
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const navigate = useCallback((next: Route) => {
    const path = routeToPath(next);
    // Preserve existing query params (e.g. this demo's own `?config=` override) while updating/
    // clearing `accessToken` for the new route - a plain string-append would duplicate or lose one.
    const params = new URLSearchParams(window.location.search);
    if ((next.name === 'checkout' || next.name === 'order') && next.accessToken) {
      params.set('accessToken', next.accessToken);
    } else {
      params.delete('accessToken');
    }
    const qs = params.toString();
    window.history.pushState({}, '', path + (qs ? `?${qs}` : ''));
    setRoute(next);
    window.scrollTo(0, 0);
  }, []);

  const value = useMemo(() => ({ route, navigate }), [route, navigate]);
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

export function useRouter(): RouterContextValue {
  const ctx = useContext(RouterContext);
  if (!ctx) throw new Error('useRouter must be used inside RouterProvider');
  return ctx;
}
